import Table, {
  TableControls,
  TableFooter,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn,
  buildBulkSelectionColumn,
  buildSourceColumn
} from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { m360Paths } from "@/lib/m360Paths";
import { Props as EmptyStateProps } from "@/components/EmptyState";
import { useNavigate, Path } from "@/router";
import { PURGE_MIN_COUNT, MERGE_PURGE_MAX_COUNT, PURGE_MAX_COUNT } from "../person-merge-purge/constants";
import { Person, ProgramTypeName, ProgramStatus } from "@/types";
import { ColumnDef, ColumnSort } from "@tanstack/react-table";

import {
  getClassificationDisplay,
  getFieldLabel,
  getPrimaryFacilityId,
  getPrimaryFacilityName,
  getPrimarySectionId,
  getPrimarySectionName,
  getProgramName,
  getProgramStatus
} from "../_utils";
import { renderName } from "@/lib/personHelpers";
import PeopleSearchInput from "./PeopleSearchInput";
import ListTableCopyButton from "@/pages/_components/ListTableCopyButton";
import { useState, useEffect, useMemo, useCallback, type ReactNode } from "react";
import type { Action } from "@/components/MenuButton";
import {
  PEOPLE_MEMBERSHIP_QUERY,
  PeopleQueryResponse,
  PeopleQueryVariables,
  QUICKSEARCH_OPTIONS_QUERY
} from "../queries";
import { generatePath } from "react-router-dom";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { useQuery } from "@apollo/client";
import { GET_CLASSIFICATIONS } from "../../billing/queries";
import MultiSelect from "@/components/MultiSelect";
import { reject, isEmpty } from "lodash";
import { Query } from "../_hooks/useSearchParams";
import TextField from "@/components/TextField";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ProgramStatusTag } from "@/features/person/components/PersonObjectHeading";
import { getDisplayedCcmsSuspension, getSuspensionReasonLabel } from "@/features/person/utils/utils";
import { useCanManageInteractionsBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider/types";
import type { useBulkSelection } from "@/hooks/useBulkSelection";
import Button from "@/components/Button";
import Tooltip from "@/components/Tooltip";
import useAmplitude from "@/hooks/useAmplitude";
import { PEOPLE_SEARCH_ANALYTICS_EVENTS } from "../_utils/amplitude";
import { LookupPersonInCcms, parsePgaIdQuery, useCanShowCcmsMemberLookup } from "@/features/person/ccmsLookup";

function buildColumns(
  setMenu: (menu: TableRowMenu) => void,
  isCompactLayout: boolean,
  canManageInteractions: boolean,
  navigateToInteractions: (pgaId: string) => void,
  searchParamsUrl: string,
  navigate: (path: Path) => void,
  onViewPerson?: (pgaId: string) => void,
  onNavigateToPerson?: (pgaId: string) => void,
  onSuspendedStatusTooltipHover?: () => void,
  canViewPersonMembership = true,
  hideSectionColumn = false
): ColumnDef<Person, any>[] {
  const classColumn: ColumnDef<Person, any> = {
    id: "class",
    header: "Class",
    size: 280,
    accessorFn: (person) => getClassificationDisplay(person),
    cell: ({ getValue }) => renderEmptyCellState(getValue()),
    enableSorting: false
  };

  const statusColumn: ColumnDef<Person, any> = {
    id: "status",
    header: "Status",
    enableSorting: false,
    meta: {
      headerClassName: "hide-header-in-column",
      skipEmptyState: true
    },
    cell: (cellProps: { row: { original: Person } }) => {
      const person = cellProps.row.original;
      const status = getProgramStatus(person);
      if (!status) {
        return null;
      }
      const activeSuspension = getDisplayedCcmsSuspension(status, person.ccmsSuspensions);
      const suspensionReason = activeSuspension ? getSuspensionReasonLabel(activeSuspension) : undefined;
      const statusTag = (
        <ProgramStatusTag
          status={status}
          terminatedReason={person.latestProgram?.band?.terminatedReason}
          terminatesOn={person.latestProgram?.band?.terminatesOn}
          suspensionReason={suspensionReason}
          suspensionStartDate={activeSuspension?.startDate}
          pendingTerminatesOn={person.latestProgram?.band?.expiresOn}
        />
      );

      if (status === "Suspended" && suspensionReason && onSuspendedStatusTooltipHover) {
        return <span onMouseEnter={onSuspendedStatusTooltipHover}>{statusTag}</span>;
      }

      return statusTag;
    }
  };

  const columns: ColumnDef<Person, any>[] = [
    ...buildAvatarDetailsColumn<Person>({
      isCompactLayout,
      getTitle: (person) =>
        renderName(
          person.displayName,
          person.informalName,
          person.preferredLastName,
          person.suffixName,
          person.middleName,
          person.firstName
        ),
      getSubtitle: (person) => getProgramName(person),
      getPicture: (person) => person.profilePhoto,
      header: "Name",
      size: 230,
      id: "displayName",
      sortType: "text"
    }),
    buildCopyTextColumn<Person>({
      accessorKey: "pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      CopyButtonComponent: ListTableCopyButton,
      getText: (person) => person.pgaId || "",
      shouldShowCopyButton: (person) => !!person.pgaId,
      sortType: "text"
    }),
    ...(canViewPersonMembership ? [classColumn] : []),
    buildSourceColumn<Person>({
      accessorKey: "section",
      header: "Section",
      size: 200,
      sortType: "text",
      accessorFn: (person) => getPrimarySectionName(person),
      getText: (person) => getPrimarySectionName(person),
      getParentLink: (person) => {
        const sectionId = getPrimarySectionId(person);
        return sectionId ? m360Paths.section(sectionId) : null;
      },
      navigate: (to) => navigate(to as Path),
      meta: { headerClassName: "hide-header-in-column" }
    }),
    buildSourceColumn<Person>({
      accessorKey: "primaryFacility",
      header: "Primary Facility",
      size: 300,
      sortType: "text",
      accessorFn: (person) => getPrimaryFacilityName(person),
      getText: (person) => getPrimaryFacilityName(person),
      getParentLink: (person) => {
        const name = getPrimaryFacilityName(person);
        const facilityId = getPrimaryFacilityId(person);
        if (!facilityId || !name || name === "Unemployed") return null;
        return m360Paths.facility(facilityId);
      },
      navigate: (to) => navigate(to as Path)
    }),
    ...(canViewPersonMembership ? [statusColumn] : []),
    buildActionColumn<Person & { id: string }>({
      getActions: (person) => [
        ...(person.pgaId
          ? [
              {
                text: "View Person",
                onClick: () => {
                  if (onNavigateToPerson) {
                    onNavigateToPerson(person.pgaId!);
                    return;
                  }
                  const personPath = buildPersonPath(person.pgaId, searchParamsUrl, isCompactLayout);
                  if (personPath) {
                    navigate(personPath);
                  }
                }
              } as const
            ]
          : []),
        ...(canManageInteractions && person.pgaId
          ? [
              {
                text: "Add Interaction",
                onClick: () => {
                  navigateToInteractions(person.pgaId!);
                }
              } as const
            ]
          : [])
      ],
      onViewClick: onViewPerson
        ? (person) => {
            if (person.pgaId) {
              onViewPerson(person.pgaId);
            }
          }
        : undefined,
      showViewButton: (person) => !!person.pgaId,
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (person) => person.pgaId || person.id
    })
  ];

  return hideSectionColumn ? columns.filter((column) => column.id !== "section") : columns;
}

function buildPersonPath(
  pgaId: string | null | undefined,
  searchParamsUrl: string,
  isCompactLayout = false,
  returnTo = "/people"
) {
  if (!pgaId) {
    return null;
  }
  if (isCompactLayout) {
    return `${generatePath(`/people/:pgaId`, { pgaId })}?return_to=${returnTo}&${searchParamsUrl}` as Path;
  }
  return generatePath(`/people/:pgaId/person/overview?return_to=${returnTo}&${searchParamsUrl}`, { pgaId }) as Path;
}

export type Props = {
  loading: boolean;
  data?: PeopleQueryResponse;
  createHeaderPagination: (data?: PeopleQueryResponse["people"]) => ReactNode;
  createFooterPagination: (data?: PeopleQueryResponse["people"]) => ReactNode;
  debouncedSearch: string;
  onDebouncedSearchChange: (value: string) => void;
  searchClearSignal: number;
  clearSearch: () => void;
  emptyAction?: EmptyStateProps["action"];
  addSearchParam: (_key: string, _value: string) => void;
  deleteSearchParams: (_key: string | string[]) => void;
  searchParamsUrl: string;
  setMenu: (_menu: TableRowMenu) => void;
  advancedSearchQuery?: Partial<Query>;
  onViewPerson?: (pgaId: string) => void;
  orderBy: ColumnSort[];
  handleSort: (updaterOrValue: ColumnSort[] | ((old: ColumnSort[]) => ColumnSort[])) => void;
  bulkSelection?: ReturnType<typeof useBulkSelection>;
  lockedSectionId?: string;
  personReturnTo?: string;
  peopleQueryVariables?: PeopleQueryVariables;
  actions?: Action[];
};

export default function PeopleTable({
  loading,
  data,
  createHeaderPagination,
  createFooterPagination,
  debouncedSearch,
  onDebouncedSearchChange,
  searchClearSignal,
  clearSearch,
  addSearchParam,
  deleteSearchParams,
  searchParamsUrl,
  setMenu,
  advancedSearchQuery,
  onViewPerson,
  orderBy,
  handleSort,
  bulkSelection,
  lockedSectionId,
  personReturnTo = "/people",
  peopleQueryVariables,
  actions
}: Props) {
  const { isCompactLayout } = useIsCompactLayout();
  const canManageInteractions = useCanManageInteractionsBool();
  const canViewPersonMembership = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonMembership
  });
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const canManageMergePurge = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageMergePurge });
  const canShowCcmsMemberLookup = useCanShowCcmsMemberLookup();
  const pgaIdQuery = useMemo(() => parsePgaIdQuery(debouncedSearch), [debouncedSearch]);

  const handleCcmsPersonFound = useCallback(
    (pgaId: string) => {
      navigate(
        buildPersonPath(pgaId, searchParamsUrl, isCompactLayout, personReturnTo) || (`/people/${pgaId}` as Path)
      );
    },
    [isCompactLayout, navigate, personReturnTo, searchParamsUrl]
  );
  const isMergePurgeEnabled = canManageMergePurge && !!bulkSelection;
  const rowSelection = bulkSelection?.rowSelection ?? {};
  const setRowSelection = bulkSelection?.setRowSelection;
  const bulkSelectionProps = bulkSelection?.bulkSelectionProps;
  const deselectAll = bulkSelection?.deselectAll;
  const rowSelectionCount = bulkSelection?.rowSelectionCount ?? 0;
  const orderedSelectedIds = bulkSelection?.orderedSelectedIds ?? [];
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const { data: membershipData } = useQuery(PEOPLE_MEMBERSHIP_QUERY, {
    variables: peopleQueryVariables,
    skip: !canViewPersonMembership || !peopleQueryVariables,
    fetchPolicy: "network-only"
  });
  const { data: sectionData } = useQuery(QUICKSEARCH_OPTIONS_QUERY, {
    fetchPolicy: "cache-first",
    skip: !!lockedSectionId
  });
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>([]);
  const [selectedProgramValues, setSelectedProgramValues] = useState<string[]>([]);
  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>([]);
  const [selectedSectionValues, setSelectedSectionValues] = useState<string[]>([]);

  const peopleData = useMemo(() => data?.people?.nodes ?? [], [data?.people?.nodes]);
  const membershipByPgaId = useMemo(() => {
    const map = new Map<string, Person>();
    membershipData?.people?.nodes?.forEach((person) => {
      if (person.pgaId) {
        map.set(person.pgaId, person);
      }
    });
    return map;
  }, [membershipData?.people?.nodes]);

  const peopleDataWithMembership = useMemo(
    () =>
      peopleData.map((person) => {
        const membershipFields = person.pgaId ? membershipByPgaId.get(person.pgaId) : undefined;
        if (!membershipFields) return person;
        return {
          ...person,
          latestProgram: membershipFields.latestProgram,
          ccmsSuspensions: membershipFields.ccmsSuspensions
        };
      }),
    [membershipByPgaId, peopleData]
  );

  const peopleDataWithIds = peopleDataWithMembership.map((person, index) => ({
    ...person,
    id: person.pgaId || `person-${index}`
  }));

  const trackPeopleSearchResult = useCallback(() => {
    if (!debouncedSearch.trim()) return;
    trackEvent(PEOPLE_SEARCH_ANALYTICS_EVENTS.clickPeopleSearchResult, {
      backofficeObjectType: "Person"
    });
  }, [debouncedSearch, trackEvent]);

  const trackPeopleSuspendedStatusTooltipHover = useCallback(() => {
    trackEvent(PEOPLE_SEARCH_ANALYTICS_EVENTS.hoverPeopleSuspendedStatusTooltip);
  }, [trackEvent]);

  const handleViewPerson = useCallback(
    (pgaId: string) => {
      trackPeopleSearchResult();
      onViewPerson?.(pgaId);
    },
    [onViewPerson, trackPeopleSearchResult]
  );

  const handleNavigateToPerson = useCallback(
    (pgaId: string) => {
      trackPeopleSearchResult();
      const personPath = buildPersonPath(pgaId, searchParamsUrl, isCompactLayout, personReturnTo);
      if (personPath) {
        navigate(personPath);
      }
    },
    [isCompactLayout, navigate, personReturnTo, searchParamsUrl, trackPeopleSearchResult]
  );

  const navigateToInteractions = useCallback(
    (pgaId: string) => {
      navigate(`/people/${pgaId}/interactions/interactions` as Path, {
        state: { autoOpenSidebar: true }
      });
    },
    [navigate]
  );

  const classificationNodes = classificationData?.classifications?.nodes || [];
  const classificationDataNodes = classificationNodes.map((node: any) => ({
    value: node.name,
    label: node.name
  }));

  const sectionOptions = sectionData?.crmSections.nodes.map(({ id, name }) => ({ value: id, label: name })) ?? [];
  const statusOptions = Object.keys(ProgramStatus).map((value) => ({
    value: value,
    label: value.charAt(0) + value.slice(1).toLowerCase()
  }));

  const programOptions = Object.keys(ProgramTypeName).map((value) => ({
    value: value.toUpperCase(),
    label: value.charAt(0) + value.slice(1).toLowerCase()
  }));

  const isMergePurgeDisabled = rowSelectionCount < PURGE_MIN_COUNT || rowSelectionCount > MERGE_PURGE_MAX_COUNT;
  const bulkActionLabel = rowSelectionCount <= PURGE_MAX_COUNT ? "Purge Record" : "Merge/Purge Records";
  const bulkActionType = rowSelectionCount <= PURGE_MAX_COUNT ? "purge" : "merge-purge";

  const columns = useMemo(
    () => [
      ...(isMergePurgeEnabled ? [buildBulkSelectionColumn<Person & { id: string }>()] : []),
      ...buildColumns(
        setMenu,
        isCompactLayout,
        canManageInteractions,
        navigateToInteractions,
        searchParamsUrl,
        navigate,
        onViewPerson ? handleViewPerson : undefined,
        handleNavigateToPerson,
        trackPeopleSuspendedStatusTooltipHover,
        canViewPersonMembership,
        !!lockedSectionId
      )
    ],
    [
      isMergePurgeEnabled,
      setMenu,
      isCompactLayout,
      canManageInteractions,
      navigateToInteractions,
      searchParamsUrl,
      navigate,
      onViewPerson,
      handleViewPerson,
      handleNavigateToPerson,
      trackPeopleSuspendedStatusTooltipHover,
      canViewPersonMembership,
      lockedSectionId
    ]
  );

  const bulkActions = isMergePurgeEnabled
    ? [
        {
          label: bulkActionLabel,
          variant: "outlined" as const,
          disabled: isMergePurgeDisabled,
          onClick: () => {
            navigate(`/people/person-merge-purge?action=${bulkActionType}&ids=${orderedSelectedIds.join(",")}` as Path);
          },
          render: isMergePurgeDisabled
            ? () => (
                <Tooltip
                  supportingText={`Select ${PURGE_MIN_COUNT}-${MERGE_PURGE_MAX_COUNT} people to merge/purge`}
                  variant="plain"
                  color="black"
                  positioning="fixed"
                >
                  <Button variant="outlined" disabled={true} onClick={() => {}}>
                    {bulkActionLabel}
                  </Button>
                </Tooltip>
              )
            : undefined
        }
      ]
    : [];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const getArrayParam = (key: string): string[] => {
      const value = params.get(key);
      return value ? value.split(",") : [];
    };

    if (params.has("memberClassifications[]")) {
      setSelectedClassificationValues(getArrayParam("memberClassifications[]"));
    }
    if (params.has("programTypes[]")) {
      setSelectedProgramValues(getArrayParam("programTypes[]"));
    }
    if (params.has("statuses[]")) {
      setSelectedStatusValues(getArrayParam("statuses[]"));
    }
    if (params.has("sectionIds[]") && !lockedSectionId) {
      setSelectedSectionValues(getArrayParam("sectionIds[]"));
    }
  }, [lockedSectionId]);

  useEffect(() => {
    if (advancedSearchQuery) {
      if (advancedSearchQuery.memberClassifications?.length) {
        setSelectedClassificationValues(advancedSearchQuery.memberClassifications);
      }
      if (advancedSearchQuery.programTypes?.length) {
        setSelectedProgramValues(advancedSearchQuery.programTypes);
      }
      if (advancedSearchQuery.statuses?.length) {
        setSelectedStatusValues(advancedSearchQuery.statuses);
      }
      if (advancedSearchQuery.sectionIds?.length && !lockedSectionId) {
        setSelectedSectionValues(advancedSearchQuery.sectionIds);
      }
    }
  }, [advancedSearchQuery, lockedSectionId]);

  const filterState = {
    memberClassifications: selectedClassificationValues.length > 0,
    programTypes: selectedProgramValues.length > 0,
    statuses: selectedStatusValues.length > 0,
    sectionIds: !lockedSectionId && selectedSectionValues.length > 0,
    email: !!advancedSearchQuery?.email,
    phone: !!advancedSearchQuery?.phone,
    contactPostalCode: !!advancedSearchQuery?.contactPostalCode
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    clearSearch();
    setSelectedClassificationValues([]);
    setSelectedProgramValues([]);
    setSelectedStatusValues([]);
    if (!lockedSectionId) {
      setSelectedSectionValues([]);
    }
    deleteSearchParams(
      lockedSectionId
        ? ["memberClassifications[]", "programTypes[]", "statuses[]", "email", "phone", "contactPostalCode"]
        : [
            "memberClassifications[]",
            "programTypes[]",
            "statuses[]",
            "sectionIds[]",
            "email",
            "phone",
            "contactPostalCode"
          ]
    );
  };

  return (
    <Table
      data={peopleDataWithIds}
      loading={loading}
      getRowLink={(row) => {
        const personPath = buildPersonPath(row.original.pgaId, searchParamsUrl, isCompactLayout, personReturnTo);
        return personPath
          ? {
              to: personPath as any,
              onClick: trackPeopleSearchResult
            }
          : undefined;
      }}
      onSortingChange={handleSort}
      sortingState={orderBy}
      serverSideSorting={true}
      {...(isMergePurgeEnabled && {
        enableRowSelection: true,
        rowSelection,
        onRowSelectionChange: setRowSelection
      })}
      hasActiveFilters={activeFilterCount > 0 || !!debouncedSearch}
      {...(canShowCcmsMemberLookup && pgaIdQuery && activeFilterCount === 0
        ? {
            renderFilteredEmptyState: () => (
              <LookupPersonInCcms pgaId={pgaIdQuery} onFound={handleCcmsPersonFound} variant="emptyState" />
            )
          }
        : {})}
      renderControls={(table, { onSortClick }) => (
        <TableControls
          pagination={createHeaderPagination(data?.people)}
          actions={actions}
          onSortClick={onSortClick}
          useExpandedFilters={true}
          expandedFiltersState={filterState}
          onClearAllFilters={handleClearAllFilters}
          activeFilters={activeFilterCount}
          {...(isMergePurgeEnabled && {
            bulkActions,
            ...bulkSelectionProps,
            onDeselectAll: deselectAll,
            totalRowCount: rowSelectionCount,
            onToggleAllRows: (checked: boolean) => table.toggleAllRowsSelected(checked),
            allRowsSelected: table.getIsAllRowsSelected(),
            someRowsSelected: table.getIsSomeRowsSelected()
          })}
        >
          <PeopleSearchInput onDebouncedChange={onDebouncedSearchChange} clearSignal={searchClearSignal} />
          <div data-name="memberClassifications">
            <MultiSelect
              label="Classification"
              selectedValues={selectedClassificationValues}
              options={classificationDataNodes}
              onSelect={(value, checked) => {
                const status = checked
                  ? [...selectedClassificationValues, value]
                  : selectedClassificationValues.filter((v) => v !== value);
                setSelectedClassificationValues(reject(status, isEmpty));
                addSearchParam("memberClassifications[]", reject(status, isEmpty).join(","));
              }}
              onClear={() => {
                setSelectedClassificationValues([]);
                deleteSearchParams("memberClassifications[]");
              }}
              className="w-full"
            />
          </div>
          <div data-name="programTypes">
            <MultiSelect
              label="Program"
              selectedValues={selectedProgramValues}
              options={programOptions}
              onSelect={(value, checked) => {
                const status = checked
                  ? [...selectedProgramValues, value]
                  : selectedProgramValues.filter((v) => v !== value);
                setSelectedProgramValues(reject(status, isEmpty));
                addSearchParam("programTypes[]", reject(status, isEmpty).join(","));
              }}
              onClear={() => {
                setSelectedProgramValues([]);
                deleteSearchParams("programTypes[]");
              }}
              className="w-full"
            />
          </div>
          <div data-name="statuses">
            <MultiSelect
              label="Status"
              selectedValues={selectedStatusValues}
              options={statusOptions}
              onSelect={(value, checked) => {
                const status = checked
                  ? [...selectedStatusValues, value]
                  : selectedStatusValues.filter((v) => v !== value);
                setSelectedStatusValues(reject(status, isEmpty));
                addSearchParam("statuses[]", reject(status, isEmpty).join(","));
              }}
              onClear={() => {
                setSelectedStatusValues([]);
                deleteSearchParams("statuses[]");
              }}
              className="w-full"
            />
          </div>
          {!lockedSectionId && (
            <div
              data-name="sectionIds"
              data-filter-size="custom"
              className="min-w-[280px]"
              data-testid="people-section-filter"
            >
              <MultiSelect
                label="Section"
                selectedValues={selectedSectionValues}
                options={sectionOptions}
                onSelect={(value, checked) => {
                  const status = checked
                    ? [...selectedSectionValues, value]
                    : selectedSectionValues.filter((v) => v !== value);
                  setSelectedSectionValues(reject(status, isEmpty));
                  addSearchParam("sectionIds[]", reject(status, isEmpty).join(","));
                }}
                onClear={() => {
                  setSelectedSectionValues([]);
                  deleteSearchParams("sectionIds[]");
                }}
                className="w-full"
              />
            </div>
          )}
          <div data-name="email">
            <TextField
              label={getFieldLabel("email")}
              type="email"
              value={advancedSearchQuery?.email || ""}
              onChangeText={(value: string) => {
                if (value) {
                  addSearchParam("email", value);
                } else {
                  deleteSearchParams("email");
                }
              }}
              className="w-full"
            />
          </div>
          <div data-name="phone">
            <TextField
              label={getFieldLabel("phone")}
              value={advancedSearchQuery?.phone || ""}
              onChangeText={(value: string) => {
                if (value) {
                  addSearchParam("phone", value);
                } else {
                  deleteSearchParams("phone");
                }
              }}
              className="w-full"
            />
          </div>
        </TableControls>
      )}
      columns={columns}
      renderFooter={() => (
        <TableFooter
          pagination={createFooterPagination(data?.people)}
          {...(isMergePurgeEnabled && bulkSelectionProps)}
        />
      )}
    />
  );
}
